import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";

/**
 * What onboarding will do to one existing checkout experience.
 *   enable        — reached only internationally; turn landed costs on in place.
 *   clone         — also reached domestically; copy it, enable on the copy, and
 *                   repoint just the international paths at the copy.
 *   alreadyOn     — landed costs are already enabled.
 *   incompatible  — its shipping methods can't carry landed costs; left alone.
 */
export const LandedCostOnboardExperienceAction = z.enum([
  "enable",
  "clone",
  "alreadyOn",
  "incompatible",
]);
export type LandedCostOnboardExperienceAction = z.infer<
  typeof LandedCostOnboardExperienceAction
>;

const LandedCostOnboardExperienceSchema = z.object({
  checkoutExperienceId: z.string(),
  name: z.string(),
  action: LandedCostOnboardExperienceAction,
  /** Shipping method names blocking landed costs, for `incompatible` only. */
  incompatibleShippingMethods: z.array(z.string()),
});

/**
 * `addCrossBorderBranch` — the tree has no international branch, so a
 * cross-border branch goes on top and the existing tree keeps serving everyone
 * else. `updateExistingExperiences` — it already routes international traffic,
 * so those experiences get landed costs instead.
 */
export const LandedCostOnboardMode = z.enum([
  "addCrossBorderBranch",
  "updateExistingExperiences",
]);
export type LandedCostOnboardMode = z.infer<typeof LandedCostOnboardMode>;

const LandedCostOnboardPlanSchema = z.object({
  mode: LandedCostOnboardMode,
  homeCountryCode: z.string(),
  experiences: z.array(LandedCostOnboardExperienceSchema),
  /** True when the team has no tree yet, so onboarding creates one. */
  createsCheckoutTree: z.boolean().optional(),
  /** True when a live demo wrapper will be replaced by going live. */
  replacesDemo: z.boolean().optional(),
});
export type LandedCostOnboardPlan = z.infer<typeof LandedCostOnboardPlanSchema>;

export const previewLandedCostOnboardCheckoutTree = {
  name: "previewLandedCostOnboardCheckoutTree" as const,
  input: z.object({ teamId: zExt.objectId() }),
  output: LandedCostOnboardPlanSchema,
};

export const applyLandedCostOnboardCheckoutTree = {
  name: "applyLandedCostOnboardCheckoutTree" as const,
  input: z.object({ teamId: zExt.objectId() }),
  output: z.object({
    mode: LandedCostOnboardMode,
    enabledCount: z.number(),
    clonedCount: z.number(),
    skipped: z.array(z.string()),
  }),
};
